import { hoursToMinutes, minutesToHours } from '@acme/common'
import { z } from 'zod'

const MINUTOS_MAXIMOS = 23 * 60 + 59

/** Converte tempo `HH:mm` (formato usado pela API) em minutos */
export function tempoParaMinutos(valor: string | number) {
    if (typeof valor === 'number') return valor

    const texto = valor.trim()
    if (!texto) return 0

    if (!texto.includes(':')) return Number(texto)

    return hoursToMinutes(texto)
}

/** Campo preenchido em minutos pelo usuário, enviado ao back-end como `HH:mm` */
export const minutosInput = z
    .union([
        z.number(),
        z.string().regex(/^\d*$/, 'Informe a tolerância em minutos'),
    ])
    .nullish()
    .transform(valor =>
        typeof valor === 'string' ? Number(valor || 0) : (valor ?? 0),
    )
    .refine(
        minutos =>
            Number.isInteger(minutos) &&
            minutos >= 0 &&
            minutos <= MINUTOS_MAXIMOS,
        `Tolerância deve ser um valor entre 0 e ${MINUTOS_MAXIMOS} minutos`,
    )
    .transform(minutos => minutesToHours(minutos))

/** Campo preenchido em `HH:mm` pelo usuário, enviado ao back-end em minutos; vazio vira `null` */
export const tempoEmMinutosInput = z
    .union([
        z.number(),
        z
            .string()
            .regex(/^$|^([01]?\d|2[0-3]):[0-5]\d$/, 'Informe o tempo em HH:mm'),
    ])
    .nullish()
    .transform(valor =>
        valor === '' || valor == null ? null : tempoParaMinutos(valor),
    )

export const toleranciasSchema = z.object({
    entradaAntes: minutosInput,
    entradaDepois: minutosInput,
    saidaAntes: minutosInput,
    saidaDepois: minutosInput,
    toleranciaTurno: minutosInput,
    toleranciaDia: minutosInput,
})

/** Converte as tolerâncias vindas da API (`HH:mm`) para os minutos exibidos no formulário */
export function toleranciasEmMinutos(
    tolerancias: z.output<typeof toleranciasSchema>,
) {
    return Object.fromEntries(
        Object.entries(tolerancias).map(([campo, valor]) => [
            campo,
            tempoParaMinutos(valor),
        ]),
    ) as Record<keyof z.output<typeof toleranciasSchema>, number>
}
